'use client';

import { useEffect, useState } from 'react';

import { PrV2PullRequestContextMenu } from '../pr-v2-pull-request-context-menu';
import { PrV2CopyNumberButton } from './pr-v2-copy-number-button';

import { usePrV2Summary } from '@/data/v2/pr/use-pr-v2-summary';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

type PrV2AnimatedTitleProps = {
  showNumber?: boolean;
  owner: string;
  repo: string;
  number: string;
};

// Source title element rendered in the page body; this component mirrors it in top navigation.
const TITLE_TARGET_SELECTOR = '[data-pr-v2-title="true"]';
const TITLE_INTERSECTION_TOP_OFFSET_PX = 64;
const TITLE_INTERSECTION_ROOT_MARGIN = `-${TITLE_INTERSECTION_TOP_OFFSET_PX}px 0px 0px 0px`;
// Stop observing after 10s so a missing title node cannot leave a long-lived body observer running.
const TITLE_MUTATION_OBSERVER_TIMEOUT_MS = 10000;

/**
 * Shows a compact sticky title in top nav once the main PR title scrolls out of view.
 */
export function PrV2AnimatedTitle({
  showNumber = true,
  owner,
  repo,
  number,
}: PrV2AnimatedTitleProps) {
  const [isTitleVisible, setIsTitleVisible] = useState(false);
  const isMobile = useIsMobile();
  const summaryQuery = usePrV2Summary(owner, repo, number);
  const title = summaryQuery.data?.pullRequest?.title?.trim() ?? '';
  const showExpandedTitle = isTitleVisible && !isMobile && title.length > 0;
  const showMobileTitle = isMobile && title.length > 0;

  useEffect(() => {
    let intersectionObserver: IntersectionObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let mutationObserverTimeout: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    function observeTitle(titleElement: Element) {
      intersectionObserver?.disconnect();
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) {
            return;
          }

          // The page title node is replaced whenever it changes state (loading
          // skeleton -> rendered -> editing). A detached node reports
          // isIntersecting=false, which would wrongly pop the nav title in
          // while the user is still at the top of the page — keep it hidden
          // and re-discover the replacement node instead.
          if (!entry.target.isConnected) {
            setIsTitleVisible(false);
            armTitleDiscovery();
            return;
          }

          setIsTitleVisible(!entry.isIntersecting);
        },
        {
          threshold: 0,
          rootMargin: TITLE_INTERSECTION_ROOT_MARGIN,
        },
      );

      intersectionObserver.observe(titleElement);
    }

    function findAndObserveTitle() {
      const titleElement = document.querySelector(TITLE_TARGET_SELECTOR);
      if (!titleElement) {
        return false;
      }

      observeTitle(titleElement);
      return true;
    }

    function armTitleDiscovery() {
      if (disposed || findAndObserveTitle() || mutationObserver) {
        return;
      }

      // Title mounts after async summary load; observe DOM mutations until it appears.
      mutationObserver = new MutationObserver((_mutations, observer) => {
        if (findAndObserveTitle()) {
          observer.disconnect();
          mutationObserver = null;
          if (mutationObserverTimeout) {
            clearTimeout(mutationObserverTimeout);
            mutationObserverTimeout = null;
          }
        }
      });

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
      mutationObserverTimeout = setTimeout(() => {
        mutationObserver?.disconnect();
        mutationObserver = null;
      }, TITLE_MUTATION_OBSERVER_TIMEOUT_MS);
    }

    armTitleDiscovery();

    return () => {
      disposed = true;
      if (mutationObserverTimeout) {
        clearTimeout(mutationObserverTimeout);
      }
      mutationObserver?.disconnect();
      intersectionObserver?.disconnect();
    };
  }, [owner, repo, number]);

  return (
    <PrV2PullRequestContextMenu owner={owner} repo={repo} number={number}>
      <span
        className={cn(
          'flex min-w-0 flex-1 items-center overflow-hidden md:-mb-px md:max-w-[400px]',
          showNumber && 'md:ml-2',
        )}
      >
        {!isMobile && showNumber ? (
          <PrV2CopyNumberButton
            number={number}
            className={cn(
              'mr-2 transition-all duration-200',
              showExpandedTitle ? 'text-zinc-400' : 'text-zinc-50',
            )}
          />
        ) : null}
        <span
          className={cn(
            'min-w-0 truncate transition-all duration-200',
            showMobileTitle
              ? 'max-w-full text-sm text-zinc-100'
              : showExpandedTitle
                ? 'visible max-w-[400px] pointer-events-auto translate-x-0 opacity-100'
                : 'invisible max-w-0 pointer-events-none -translate-x-2 opacity-0',
          )}
        >
          {title}
        </span>
      </span>
    </PrV2PullRequestContextMenu>
  );
}
